import { Check } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export interface StepperStep {
  /** Libellé visible (i18n au point d'appel). */
  label: string
}

/**
 * Stepper grand (wizard réservation/vœux) - DESIGN §8. États done/active/upcoming.
 * `aria-current="step"` sur l'étape active. `ariaLabel` (nav) et libellés fournis i18n.
 * `formatStepAria(index, total, label)` permet une annonce SR « Étape X sur N : … ».
 */
export function Stepper({
  steps,
  current,
  ariaLabel,
  formatStepAria,
  sticky = false,
  className,
}: {
  steps: StepperStep[]
  /** Index 0-based de l'étape active. */
  current: number
  ariaLabel: string
  formatStepAria?: (index: number, total: number, label: string) => string
  sticky?: boolean
  className?: string
}) {
  return (
    <nav
      aria-label={ariaLabel}
      className={cn(
        'z-20 border-b border-[var(--v2-line)] bg-[var(--v2-surface)]/95 backdrop-blur',
        sticky && 'sticky top-0',
        className,
      )}
    >
      <ol
        // Scrollable region with no focusable children must itself be keyboard-focusable (WCAG 2.1.1).
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className="flex items-center gap-2 overflow-x-auto px-1 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {steps.map((step, index) => {
          const state = index < current ? 'done' : index === current ? 'active' : 'upcoming'
          const ariaCurrent = state === 'active' ? 'step' : undefined
          const stepAria = formatStepAria?.(index, steps.length, step.label)

          return (
            <li
              key={index}
              aria-current={ariaCurrent}
              aria-label={stepAria}
              className="flex shrink-0 items-center gap-2"
            >
              <span
                className={cn(
                  'inline-flex size-7 items-center justify-center rounded-pill text-sm font-semibold transition-colors',
                  state === 'done' && 'bg-[var(--v2-ok)] text-[var(--v2-on-solid)]',
                  state === 'active' && 'bg-[var(--v2-brand)] text-[var(--v2-on-solid)]',
                  state === 'upcoming' && 'bg-[var(--v2-bg-soft)] text-[var(--v2-ink-3)]',
                )}
              >
                {state === 'done' ? <Check className="size-4" aria-hidden /> : index + 1}
              </span>
              <span
                className={cn(
                  'text-sm',
                  state === 'active'
                    ? 'font-semibold text-[var(--v2-ink)]'
                    : 'text-[var(--v2-ink-3)]',
                )}
              >
                {step.label}
              </span>
              {index < steps.length - 1 ? (
                <span aria-hidden className="mx-1 h-px w-6 bg-[var(--v2-line)]" />
              ) : null}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
